import { useEffect, useMemo, useState } from "react";
import {
    LayoutPanelLeft, Save, Info, Loader2, Lock,
    Shirt, UserRound, Table2, Sparkles,
    Ruler, Scissors, Zap, Package, Layers,
    Monitor, Smartphone, Tablet, ArrowUpToLine, ArrowDownToLine,
    ArrowUpFromLine, ArrowDownFromLine, ArrowRight, Inbox,
    Palette, MapPin, Pencil, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { fetchAllWidgetSettings, updateWidgetSettings } from "@/lib/widget-settings-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Card, CardHeader, Field, SwitchRow, Segmented,
    ModeSegmented, ModeStatusPill, ModeBanner,
    PremiumAccentPicker, GradientPicker, WidgetConfigSkeleton,
    adaptColor, pastelColor, barGradient,
} from "@/components/widget-config/shared";
import { PageHeader, PrimaryButton } from "@/components/design-system";
import { WIDGET_TYPO } from "@/lib/sayz-widget-tokens";
import { SayzCta } from "@/components/widget-cta/SayzCta";

/* ============================================================================
   Segmented TabsList — ModeSegmented'in büyük kardeşi. Sayfa içi ana
   navigasyon için premium bir görünüm. Her sekmede ikon + metin + opsiyonel
   badge; aktif sekme bg-background + shadow-sm ile öne çıkar.
============================================================================ */
const SP_TAB_TRIGGER_CLS =
    "group relative flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 rounded-lg text-[12.5px] font-medium transition-all border border-transparent " +
    "text-muted-foreground hover:text-foreground data-[state=inactive]:hover:bg-muted/60 " +
    "data-[state=active]:bg-foreground data-[state=active]:border-foreground data-[state=active]:text-background data-[state=active]:shadow-sm " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 focus-visible:ring-offset-0";

const SP_TAB_LIST_CLS =
    "flex w-full items-center bg-white rounded-lg p-1 h-10 gap-1";

const SP_TAB_BADGE_CLS =
    "ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-muted text-[10px] font-semibold text-foreground/80 leading-none transition-colors " +
    "group-data-[state=active]:bg-background/20 group-data-[state=active]:text-background";

const TEMPLATES = [
    { value: "minimal",   label: "Sade",      desc: "Bol boşluk, sakin tipografi. Ürün konuşsun." },
    { value: "editorial", label: "Editorial", desc: "Modern lüks minimal — sans tipografi, hairline çizgiler, ikon yok." },
    { value: "bold",      label: "Bold",      desc: "Field Notes ekolü — chunky tipografi, dolu butonlar, kalın borderlar." },
];

const DEFAULT_TOKENS = {
    accent: "#2B2A27",
    radius: 8, spacing: 12, shadow: "soft", fontScale: 1,
    buttonStyle: "filled",
    iconStyle: "outline",
    fontFamily: "sans",
};
const DEFAULT_PANEL_TITLE = { enabled: false, text: "" };
const FONT_STACK = {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    serif: 'Georgia, "Times New Roman", "Playfair Display", serif',
    grotesk: '"Space Grotesk", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
};

const FIT_ICONS = [
    { value: "shirt", Icon: Shirt }, { value: "ruler", Icon: Ruler }, { value: "scissors", Icon: Scissors },
    { value: "sparkles", Icon: Sparkles }, { value: "zap", Icon: Zap }, { value: "package", Icon: Package }, { value: "layers", Icon: Layers },
];

const DEFAULT_BLOCKS = [
    {
        key: "fit_info", enabled: true, order: 0,
        content: { title: "Kalıp Bilgisi", showAdvice: true, showIndicator: true, hideNormal: false },
    },
    {
        key: "model_info", enabled: true, order: 1,
        content: { title: "Üzerindeki Model", avatar: "monogram", measure: "underline", showWornSize: true, showHeightWeight: true, showMeasurements: true },
    },
    {
        key: "find_my_size", enabled: true, order: 2,
        content: { title: "Bedenimi Bul", description: "Kendi bedeninizi 30 saniyede bulun.", buttonText: "Bedenimi Bul", modalTitle: "Bedenini Bul", subtitle: "30 saniyede sana en uyacak bedeni bulalım.", askAge: true },
    },
    {
        key: "size_table", enabled: true, order: 3,
        content: { title: "Beden Tablosu", description: "Ölçüleri tablodan karşılaştırın.", buttonText: "Ölçülere Bak", modalTitle: "", defaultUnit: "cm" },
    },
];

const BLOCK_META = {
    fit_info: { label: "Kalıp Bilgisi", Icon: Shirt, kind: "info", group: "info" },
    model_info: { label: "Model Bilgisi", Icon: UserRound, kind: "info", group: "info" },
    size_table: { label: "Beden Tablosu", Icon: Table2, kind: "action", group: "action" },
    find_my_size: { label: "Bedenimi Bul", Icon: Sparkles, kind: "action", group: "action" },
};

const GROUP_META = {
    info: { label: "Bilgi Blokları", desc: "Kullanıcıya bilgi gösteren bloklar" },
    action: { label: "Aksiyon Blokları", desc: "Modal açan buton blokları" },
};

const PLACEMENT_OPTS = [
    { value: "before", label: "Öncesine", desc: "Elemanın hemen üstüne", Icon: ArrowUpFromLine },
    { value: "after", label: "Sonrasına", desc: "Elemanın hemen altına", Icon: ArrowDownFromLine },
    { value: "prepend", label: "İçine · Başa", desc: "Elemanın içine, en üste", Icon: ArrowUpToLine },
    { value: "append", label: "İçine · Sona", desc: "Elemanın içine, en alta", Icon: ArrowDownToLine },
];

const FALLBACK = {
    product: { sku: "PRD-8201" },
    fit: { type: "slim" },
    model: { name: "Elif Kaya", height: 176, weight: 58, chest: 86, waist: 62, hip: 90, wearsSize: "M" },
    // Preview'da tüm bloklar görünsün diye kukla bir tablo — gerçek içerik
    // gerekmez, PanelPreview sadece `sample.table` truthy mi diye bakacak.
    table: { active: true },
};

export default function WidgetSizingPanel() {
    const { models, fitLabels } = useSizing();
    const [tab, setTab] = useState("blocks");
    const [mode, setMode] = useState("passive");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [template, setTemplate] = useState("minimal");
    const [theme, setTheme] = useState("light");
    const [tokens, setTokens] = useState(DEFAULT_TOKENS);
    // Not: `selector` ve `placement` state'leri backend uyumu için isim
    // değiştirilmedi; artık YALNIZCA Masaüstü'nün alanları olarak kullanılırlar.
    const [selector, setSelector] = useState("");
    const [placement, setPlacement] = useState("after");
    const [tabletSelector, setTabletSelector] = useState("");
    const [tabletPlacement, setTabletPlacement] = useState("after");
    const [mobileSelector, setMobileSelector] = useState("");
    const [mobilePlacement, setMobilePlacement] = useState("after");
    const [showDesktop, setShowDesktop] = useState(true);
    const [showTablet, setShowTablet] = useState(true);
    const [showMobile, setShowMobile] = useState(true);
    // Validation errors — populated by handleSave, cleared on user input
    const [validationErrors, setValidationErrors] = useState({});
    const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);
    // groupOrder sabit — sıralama şablonun sorumluluğunda. State backend uyumu için tutuluyor.
    const groupOrder = ["info", "action"];

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await fetchAllWidgetSettings();
                if (!alive) return;
                const w = data?.sizing_panel || {};
                if (w.mode) setMode(w.mode);
                const c = w.config || {};
                if (c.template) setTemplate(c.template);
                if (c.tokens) {
                    setTokens((p) => ({
                        ...p,
                        ...(c.tokens.accent ? { accent: c.tokens.accent } : {}),
                        ...(c.tokens.radius != null ? { radius: c.tokens.radius } : {}),
                        ...(c.tokens.spacing != null ? { spacing: c.tokens.spacing } : {}),
                        ...(c.tokens.shadow ? { shadow: c.tokens.shadow } : {}),
                        ...(c.tokens.fontScale ? { fontScale: c.tokens.fontScale } : {}),
                        ...(c.tokens.buttonStyle ? { buttonStyle: c.tokens.buttonStyle } : {}),
                        ...(c.tokens.iconStyle ? { iconStyle: c.tokens.iconStyle } : {}),
                        ...(c.tokens.fontFamily ? { fontFamily: c.tokens.fontFamily } : {}),
                    }));
                }
                if (c.selector !== undefined) setSelector(c.selector);
                if (typeof c.tabletSelector === "string") setTabletSelector(c.tabletSelector);
                if (typeof c.tabletPlacement === "string") setTabletPlacement(c.tabletPlacement);
                if (typeof c.mobileSelector === "string") setMobileSelector(c.mobileSelector);
                if (typeof c.mobilePlacement === "string") setMobilePlacement(c.mobilePlacement);
                if (c.placement !== undefined) setPlacement(c.placement);
                if (typeof c.showDesktop === "boolean") setShowDesktop(c.showDesktop);
                if (typeof c.showTablet === "boolean") setShowTablet(c.showTablet);
                if (typeof c.showMobile === "boolean") setShowMobile(c.showMobile);
                if (Array.isArray(c.blocks) && c.blocks.length) {
                    // Merge with defaults so newly added block keys still appear
                    const byKey = Object.fromEntries(c.blocks.map((b) => [b.key, b]));
                    setBlocks(DEFAULT_BLOCKS.map((db, i) => {
                        const found = byKey[db.key];
                        if (!found) return { ...db, order: i };
                        return {
                            key: db.key,
                            enabled: found.enabled !== false,
                            order: found.order != null ? found.order : i,
                            content: { ...db.content, ...(found.content || {}) },
                        };
                    }));
                }
            } catch {
                if (alive) toast.error("Sayz Widget ayarları yüklenemedi");
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, []);

    const handleSave = async () => {
        // Validation — Her aktif cihazın kendi selector ve placement'ı zorunlu.
        // Fallback yok: cihaz aktifse alanları bağımsız olarak dolmalı.
        const errors = {};
        if (showDesktop) {
            if (!selector.trim()) errors.selector = "Masaüstü CSS seçici zorunludur.";
            if (!placement) errors.placement = "Masaüstü pozisyonu seçilmelidir.";
        }
        if (showTablet) {
            if (!tabletSelector.trim()) errors.tabletSelector = "Tablet CSS seçici zorunludur.";
            if (!tabletPlacement) errors.tabletPlacement = "Tablet pozisyonu seçilmelidir.";
        }
        if (showMobile) {
            if (!mobileSelector.trim()) errors.mobileSelector = "Mobil CSS seçici zorunludur.";
            if (!mobilePlacement) errors.mobilePlacement = "Mobil pozisyonu seçilmelidir.";
        }
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            // Kullanıcıyı ilk hatalı sekmeye yönlendir
            if (tab !== "placement") setTab("placement");
            const firstError = Object.values(errors)[0];
            toast.error(firstError);
            return;
        }
        setValidationErrors({});
        setSaving(true);
        try {
            await updateWidgetSettings("sizing_panel", {
                mode,
                config: {
                    template, theme, tokens,
                    selector, placement,
                    tabletSelector, tabletPlacement,
                    mobileSelector, mobilePlacement,
                    showDesktop, showTablet, showMobile,
                    groupOrder,
                    blocks: blocks.map((b, i) => ({ ...b, order: i })),
                },
            });
            toast.success("Sayz Widget ayarları kaydedildi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally { setSaving(false); }
    };

    // Preview fit is fixed to "normal" — the fit selector was removed for minimalism.
    const sample = useMemo(() => ({
        product: { sku: FALLBACK.product.sku },
        fit: { type: "normal" },
        model: models[0] || FALLBACK.model,
        table: FALLBACK.table,
    }), [models]);

    const previewVisible = mode !== "passive";
    const toggleBlockEnabled = (key) => setBlocks((prev) => prev.map((b) => b.key === key ? { ...b, enabled: !b.enabled } : b));
    const updateBlockContent = (key, patch) => setBlocks((prev) => prev.map((b) => b.key === key ? { ...b, content: { ...b.content, ...patch } } : b));

    if (loading) return <WidgetConfigSkeleton testId="sizing-panel-skeleton" />;

    // Aktif blok etiketlerini virgülle birleştir — üst meta satırında gösterilir
    const activeBlocksLabel = blocks
        .filter((b) => b.enabled)
        .map((b) => BLOCK_META[b.key]?.label)
        .filter(Boolean)
        .join(", ");

    return (
        <div className="space-y-6" data-testid="widget-sizing-panel-page">
            <PageHeader
                icon={LayoutPanelLeft}
                title={
                    <span className="inline-flex items-center gap-2.5">
                        <span>Sayz Widget</span>
                        <ModeStatusPill mode={mode} testId="sizing-panel-status-pill" />
                    </span>
                }
                description="Müşterileriniz doğru bedeni saniyeler içinde bulsun. Widget yerleşimini, tasarımını ve içerik bloklarını buradan yönetin."
                actions={
                    <>
                        <ModeSegmented value={mode} onChange={setMode} testId="sizing-panel-mode" />
                        <PrimaryButton onClick={handleSave} disabled={saving} data-testid="sizing-panel-save-btn">
                            {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kaydediliyor</> : <><Save className="h-3.5 w-3.5" /> Kaydet</>}
                        </PrimaryButton>
                    </>
                }
            />

            <ModeBanner mode={mode} passiveCopy="Panel şu anda pasif. Ürün sayfalarında görünmüyor." testId="sizing-panel-mode-banner" />

            <Tabs value={tab} onValueChange={setTab} data-testid="sizing-panel-tabs">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-7">
                        <div className="rounded-xl border border-border bg-background overflow-hidden" data-testid="sp-tabs-panel">
                            <div className="px-5 pt-5 pb-3 border-b border-border bg-white">
                                <TabsList className={SP_TAB_LIST_CLS}>
                                    <TabsTrigger value="blocks" className={SP_TAB_TRIGGER_CLS} data-testid="sp-tab-blocks">
                                        <Layers className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>İçerik Blokları</span>
                                        <span className={SP_TAB_BADGE_CLS} data-testid="sp-tab-blocks-badge">
                                            {blocks.filter((b) => b.enabled).length}
                                        </span>
                                    </TabsTrigger>
                                    <TabsTrigger value="panel" className={SP_TAB_TRIGGER_CLS} data-testid="sp-tab-panel">
                                        <Palette className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>Panel Tasarımı</span>
                                    </TabsTrigger>
                                    <TabsTrigger value="placement" className={SP_TAB_TRIGGER_CLS} data-testid="sp-tab-placement">
                                        <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>Widget Yerleşimi</span>
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                            <div className="p-4 space-y-4">
                                <TabsContent value="panel" className="mt-0 space-y-4">
                                    <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="sp-tab-info-panel">
                                        Widget&apos;ın genel görünümünü, renklerini, tipografisini ve marka imzasını
                                        buradan yönet. Değişikliklerin sağdaki önizlemede anlık yansır.
                                    </p>
                            <Card>
                                <CardHeader title="Şablon" desc="Panelin ve tüm blokların DNA'sı — hepsi bu şablonu miras alır." />
                                <div className="p-5 pt-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="sp-template-picker">
                                        {TEMPLATES.map((tpl) => (
                                            <button
                                                key={tpl.value}
                                                type="button"
                                                onClick={() => !tpl.disabled && setTemplate(tpl.value)}
                                                disabled={tpl.disabled}
                                                data-testid={`sp-template-${tpl.value}`}
                                                className={`group relative rounded-xl border p-3 text-left transition-all ${
                                                    tpl.disabled
                                                        ? "border-border bg-muted/20 cursor-not-allowed opacity-60"
                                                        : template === tpl.value
                                                            ? "border-foreground bg-muted/40 shadow-sm"
                                                            : "border-border bg-background hover:border-foreground/30"
                                                }`}
                                            >
                                                <TemplateThumb kind={tpl.value} active={template === tpl.value} />
                                                <div className="mt-3 flex items-center justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-semibold flex items-center gap-1.5">
                                                            {tpl.label}
                                                            {tpl.disabled && (
                                                                <span className="text-[9.5px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                                                                    Yakında
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug truncate">{tpl.desc}</p>
                                                    </div>
                                                    {!tpl.disabled && template === tpl.value && (
                                                        <span className="shrink-0 h-4 w-4 rounded-full bg-foreground grid place-items-center">
                                                            <span className="h-1.5 w-1.5 rounded-full bg-background" />
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </Card>

                            <Card>
                                <CardHeader title="Vurgu Rengi" desc="Panelin marka aksanı — butonlar, çizgiler ve vurgular bunu miras alır." />
                                <div className="p-5 pt-4">
                                    <PremiumAccentPicker
                                        value={tokens.accent}
                                        onChange={(v) => setTokens({ ...tokens, accent: v })}
                                        testId="sp-accent"
                                    />
                                </div>
                            </Card>
                        </TabsContent>

                        <TabsContent value="blocks" className="mt-0 space-y-3">
                            <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="sp-tab-info-blocks">
                                Widget&apos;ta görünecek blokları aç/kapat. Aksiyon bloklarının buton metnini kalem ikonuyla değiştirebilirsin.
                                Sıralama seçtiğin şablona göre otomatik yönetilir.
                            </p>
                            <Card>
                                <CardHeader
                                    title="Widget İçeriği"
                                    desc="Widget'ta görünecek blokları buradan yönet. Aksiyon bloklarının buton metnini kalem ikonu ile değiştirebilirsin."
                                />
                                <div className="divide-y divide-border">
                                    {[
                                        ...blocks.filter((b) => BLOCK_META[b.key].group === "info"),
                                        ...blocks.filter((b) => BLOCK_META[b.key].group === "action"),
                                    ].map((b) => (
                                        <BlockRow
                                            key={b.key}
                                            block={b}
                                            onToggleEnabled={() => toggleBlockEnabled(b.key)}
                                            onContentChange={(patch) => updateBlockContent(b.key, patch)}
                                        />
                                    ))}
                                </div>
                            </Card>
                        </TabsContent>

                        <TabsContent value="placement" className="mt-0 space-y-4">
                            <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="sp-tab-info-placement">
                                Widget&apos;ın hangi cihazlarda görüneceğini seç; ardından her aktif cihaz için
                                CSS seçici ve pozisyonu bağımsız olarak yapılandır.
                            </p>

                            <Card>
                                <CardHeader
                                    title="Cihaz Görünürlüğü"
                                    desc="Panel hangi ekran boyutlarında görünsün? Her aktif cihazın kendi yerleşimi ayrıca tanımlanır."
                                />
                                <div className="p-5 pt-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                        {(() => {
                                            const activeCount = [showDesktop, showTablet, showMobile].filter(Boolean).length;
                                            const lastActive = (isChecked) => isChecked && activeCount === 1;
                                            return (
                                                <>
                                                    <DeviceToggleCard
                                                        icon={Monitor}
                                                        label="Masaüstü"
                                                        desc="≥ 1024px"
                                                        checked={showDesktop}
                                                        onChange={setShowDesktop}
                                                        disabled={lastActive(showDesktop)}
                                                        testId="sp-desktop-switch"
                                                    />
                                                    <DeviceToggleCard
                                                        icon={Tablet}
                                                        label="Tablet"
                                                        desc="768 – 1023px"
                                                        checked={showTablet}
                                                        onChange={setShowTablet}
                                                        disabled={lastActive(showTablet)}
                                                        testId="sp-tablet-switch"
                                                    />
                                                    <DeviceToggleCard
                                                        icon={Smartphone}
                                                        label="Mobil"
                                                        desc="< 768px"
                                                        checked={showMobile}
                                                        onChange={setShowMobile}
                                                        disabled={lastActive(showMobile)}
                                                        testId="sp-mobile-switch"
                                                    />
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <p className="mt-3 text-[11px] text-muted-foreground inline-flex items-start gap-1.5">
                                        <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                        En az bir cihaz aktif olmalı. Widget&apos;ı tümüyle gizlemek için sayfa üstündeki <strong className="text-foreground">Pasif</strong> modunu kullan.
                                    </p>
                                </div>
                            </Card>

                            {showDesktop && (
                                <DevicePlacementCard
                                    icon={Monitor}
                                    label="Masaüstü"
                                    placeholder=".product-info"
                                    testId="desktop"
                                    selector={selector}
                                    onSelectorChange={(v) => {
                                        setSelector(v);
                                        if (validationErrors.selector) setValidationErrors((p) => ({ ...p, selector: undefined }));
                                    }}
                                    placement={placement}
                                    onPlacementChange={(v) => {
                                        setPlacement(v);
                                        if (validationErrors.placement) setValidationErrors((p) => ({ ...p, placement: undefined }));
                                    }}
                                    selectorError={validationErrors.selector}
                                    placementError={validationErrors.placement}
                                />
                            )}

                            {showTablet && (
                                <DevicePlacementCard
                                    icon={Tablet}
                                    label="Tablet"
                                    placeholder=".tablet-product-info"
                                    testId="tablet"
                                    selector={tabletSelector}
                                    onSelectorChange={(v) => {
                                        setTabletSelector(v);
                                        if (validationErrors.tabletSelector) setValidationErrors((p) => ({ ...p, tabletSelector: undefined }));
                                    }}
                                    placement={tabletPlacement}
                                    onPlacementChange={(v) => {
                                        setTabletPlacement(v);
                                        if (validationErrors.tabletPlacement) setValidationErrors((p) => ({ ...p, tabletPlacement: undefined }));
                                    }}
                                    selectorError={validationErrors.tabletSelector}
                                    placementError={validationErrors.tabletPlacement}
                                />
                            )}

                            {showMobile && (
                                <DevicePlacementCard
                                    icon={Smartphone}
                                    label="Mobil"
                                    placeholder=".mobile-product-info"
                                    testId="mobile"
                                    selector={mobileSelector}
                                    onSelectorChange={(v) => {
                                        setMobileSelector(v);
                                        if (validationErrors.mobileSelector) setValidationErrors((p) => ({ ...p, mobileSelector: undefined }));
                                    }}
                                    placement={mobilePlacement}
                                    onPlacementChange={(v) => {
                                        setMobilePlacement(v);
                                        if (validationErrors.mobilePlacement) setValidationErrors((p) => ({ ...p, mobilePlacement: undefined }));
                                    }}
                                    selectorError={validationErrors.mobileSelector}
                                    placementError={validationErrors.mobilePlacement}
                                />
                            )}
                        </TabsContent>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-20">
                            <Card>
                                <CardHeader
                                    title="Önizleme"
                                    desc="Widget'ınız sitenizde bu şekilde görünecektir."
                                />
                                <div className="p-4 bg-background rounded-b-xl">
                                    {/* Beyaz widget kapsayıcısı — meta bilgileri kaldırıldı */}
                                    <div className="rounded-lg bg-surface border border-border p-5">
                                        <PreviewCanvas
                                            theme={theme}
                                            previewVisible={previewVisible}
                                            template={template}
                                            tokens={tokens}
                                            blocks={blocks}
                                            sample={sample}
                                            groupOrder={groupOrder}
                                            fitLabels={fitLabels}
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </Tabs>
        </div>
    );
}

/* ============================================================================
 * Preview Canvas — neutral surface, only the widget shown (no fake product mock)
 * ============================================================================ */
const deviceLabel = (d, t, m) => {
    const parts = [];
    if (d) parts.push("Masaüstü");
    if (t) parts.push("Tablet");
    if (m) parts.push("Mobil");
    if (parts.length === 3) return "Tüm cihazlar";
    if (parts.length === 0) return "Gizli";
    return parts.join(" + ");
};

const PreviewCanvas = ({ theme, previewVisible, template, tokens, blocks, sample, groupOrder, fitLabels }) => {
    const dark = theme === "dark";
    const anyActive = blocks.some((b) => b.enabled);
    return (
        <div data-testid="sizing-panel-preview">
            {!previewVisible ? (
                <PreviewInactiveState dark={dark} />
            ) : !anyActive ? (
                <PreviewEmptyState dark={dark} />
            ) : (
                <PanelPreview
                    template={template}
                    theme={theme}
                    tokens={tokens}
                    blocks={blocks}
                    sample={sample}
                    groupOrder={groupOrder}
                    fitLabels={fitLabels}
                />
            )}
        </div>
    );
};

const PreviewInactiveState = ({ dark }) => (
    <div className="text-center py-8" data-testid="sp-preview-inactive">
        <div
            className="mx-auto h-11 w-11 rounded-full grid place-items-center mb-4"
            style={{
                background: dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.04)",
                color: dark ? "rgba(255,255,255,.55)" : "rgba(0,0,0,.5)",
            }}
        >
            <Lock className="h-4 w-4" strokeWidth={1.75} />
        </div>
        <div className="text-[13px] font-medium" style={{ color: dark ? "#E8E5DF" : "#1C1B19" }}>
            Panel pasif modda
        </div>
        <p className="text-[11px] mt-1 max-w-[280px] mx-auto leading-relaxed" style={{ color: dark ? "#A6A29A" : "#6E6A63" }}>
            Sitenizde şu anda görünmüyor. Sağ üstteki modu <span className="font-medium">Aktif</span> veya <span className="font-medium">Test</span> yaparak önizlemeyi görün.
        </p>
    </div>
);

const PreviewEmptyState = ({ dark }) => (
    <div className="text-center py-8" data-testid="sp-preview-empty">
        <div
            className="mx-auto h-11 w-11 rounded-full grid place-items-center mb-4"
            style={{
                background: dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.04)",
                color: dark ? "rgba(255,255,255,.55)" : "rgba(0,0,0,.5)",
            }}
        >
            <Inbox className="h-4 w-4" strokeWidth={1.75} />
        </div>
        <div className="text-[13px] font-medium" style={{ color: dark ? "#E8E5DF" : "#1C1B19" }}>
            Gösterilecek blok yok
        </div>
        <p className="text-[11px] mt-1 max-w-[280px] mx-auto leading-relaxed" style={{ color: dark ? "#A6A29A" : "#6E6A63" }}>
            <span className="font-medium">Bloklar</span> sekmesinden en az bir bloğu aktifleştirerek widget&apos;ınızı canlandırın.
        </p>
    </div>
);

/* ============================================================================
 * Placement helpers — visual diagram + device toggle card
 * ============================================================================ */
const PlacementDiagram = ({ kind, active }) => {
    // Small SVG mini-diagram illustrating where the panel lands relative to target element
    const targetFill = active ? "currentColor" : "rgba(0,0,0,.15)";
    const panelFill = active ? "currentColor" : "rgba(0,0,0,.35)";
    return (
        <svg viewBox="0 0 88 46" className="w-full h-auto text-foreground" aria-hidden>
            {/* target element frame */}
            <rect x="10" y="10" width="68" height="26" rx="3" fill="none" stroke={targetFill} strokeWidth="1" strokeDasharray={active ? "0" : "2 2"} />
            {kind === "before" && (
                <>
                    <rect x="14" y="4" width="60" height="4" rx="1.5" fill={panelFill} />
                    <text x="44" y="27" fontSize="7" textAnchor="middle" fill={targetFill}>hedef</text>
                </>
            )}
            {kind === "after" && (
                <>
                    <rect x="14" y="38" width="60" height="4" rx="1.5" fill={panelFill} />
                    <text x="44" y="27" fontSize="7" textAnchor="middle" fill={targetFill}>hedef</text>
                </>
            )}
            {kind === "prepend" && (
                <>
                    <rect x="14" y="14" width="60" height="4" rx="1.5" fill={panelFill} />
                    <text x="44" y="31" fontSize="7" textAnchor="middle" fill={targetFill}>hedef</text>
                </>
            )}
            {kind === "append" && (
                <>
                    <rect x="14" y="28" width="60" height="4" rx="1.5" fill={panelFill} />
                    <text x="44" y="22" fontSize="7" textAnchor="middle" fill={targetFill}>hedef</text>
                </>
            )}
        </svg>
    );
};

const DeviceToggleCard = ({ icon: Icon, label, desc, checked, onChange, testId, disabled = false }) => (
    <button
        type="button"
        onClick={() => { if (!disabled) onChange(!checked); }}
        disabled={disabled}
        title={disabled ? "En az bir cihaz aktif olmalı. Widget'ı tümüyle gizlemek için sayfa üstündeki Pasif modunu kullan." : undefined}
        className={`group relative rounded-lg border p-3 text-left transition-all ${checked ? "border-foreground bg-muted/40" : "border-border bg-background hover:border-foreground/30"} ${disabled ? "cursor-not-allowed opacity-90" : ""}`}
        data-testid={testId}
        aria-pressed={checked}
        aria-disabled={disabled}
    >
        <div className="flex items-start gap-3">
            <div className={`h-9 w-9 shrink-0 rounded-md grid place-items-center transition-colors ${checked ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>
                <Icon className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{desc}</div>
            </div>
            <Switch
                checked={checked}
                onCheckedChange={(v) => { if (!disabled) onChange(v); }}
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    </button>
);

/* ============================================================================
 * DevicePlacementCard — Her aktif cihaz (Masaüstü / Tablet / Mobil) için
 * bağımsız CSS seçici + pozisyon konfigürasyonu. Fallback yok: cihaz aktifse
 * kendi selector ve placement'ı zorunludur. Card header'ı cihaz ikonu +
 * etiket ile gelir; body içinde CSS seçici Input + 4 seçenekli Pozisyon
 * picker'ı yer alır. Hatalar (validationErrors) alan altında kırmızı olarak
 * gösterilir ve onChange ile temizlenir.
 * ============================================================================ */
const DevicePlacementCard = ({
    icon: Icon, label, placeholder, testId,
    selector, onSelectorChange,
    placement, onPlacementChange,
    selectorError, placementError,
}) => (
    <Card data-testid={`sp-device-placement-${testId}`}>
        <CardHeader
            title={
                <span className="inline-flex items-center gap-2">
                    <span className="h-6 w-6 rounded-md bg-foreground text-background grid place-items-center">
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </span>
                    <span>{label} Yerleşimi</span>
                </span>
            }
            desc={`Widget ${label.toLowerCase()} cihazlarda hangi elemente göre ve nereye yerleşsin?`}
        />
        <div className="p-5 pt-4 space-y-4">
            <Field
                label={`${label} CSS seçici`}
                required
                hint={`Yalnızca ${label.toLowerCase()} cihazlarda kullanılır — diğer cihazlardan bağımsızdır.`}
            >
                <Input
                    value={selector}
                    onChange={(e) => onSelectorChange(e.target.value)}
                    className={`h-10 text-xs font-mono ${selectorError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    placeholder={placeholder}
                    aria-invalid={!!selectorError}
                    data-testid={`sp-selector-${testId}-input`}
                />
                {selectorError && (
                    <p className="text-[11px] text-destructive mt-1" data-testid={`sp-selector-${testId}-error`}>
                        {selectorError}
                    </p>
                )}
            </Field>
            <Field label={`${label} pozisyonu`} required>
                <div
                    className={`grid grid-cols-2 lg:grid-cols-4 gap-2.5 ${placementError ? "ring-1 ring-destructive/40 rounded-lg p-1 -m-1" : ""}`}
                    data-testid={`sp-placement-${testId}-picker`}
                >
                    {PLACEMENT_OPTS.map((opt) => {
                        const active = placement === opt.value;
                        const Ic = opt.Icon;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => onPlacementChange(opt.value)}
                                className={`group rounded-lg border p-3 text-left transition-all ${active ? "border-foreground bg-muted/40 shadow-sm" : "border-border bg-background hover:border-foreground/30"}`}
                                data-testid={`sp-placement-${testId}-${opt.value}`}
                            >
                                <PlacementDiagram kind={opt.value} active={active} />
                                <div className="mt-2.5 flex items-center gap-1.5">
                                    <Ic className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    <span className="text-[12px] font-medium">{opt.label}</span>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{opt.desc}</p>
                            </button>
                        );
                    })}
                </div>
                {placementError && (
                    <p className="text-[11px] text-destructive mt-1" data-testid={`sp-placement-${testId}-error`}>
                        {placementError}
                    </p>
                )}
            </Field>
        </div>
    </Card>
);

/* ============================================================================
 * Block Accordion — per-block content editor. Sıralama şablonun sorumluluğunda;
 * merchant sadece aç/kapa ve içerik özelleştirme yapar.
 * ============================================================================ */
/* ============================================================================
 * BlockRow — Compact row per block (no accordion).
 * - Info blocks: static row with icon + label + short desc + toggle
 * - Action blocks: label = buttonText (inline editable via pencil icon)
 * ============================================================================ */
const INFO_DESC = {
    fit_info: "İçerik Kalıp Bilgileri sayfasından gelir",
    model_info: "İçerik Modeller sayfasından gelir",
};
const ACTION_DESC = "Panelde CTA butonu olarak görünür";

const BlockRow = ({ block, onToggleEnabled, onContentChange }) => {
    const meta = BLOCK_META[block.key];
    const Icon = meta.Icon;
    const isAction = meta.kind === "action";
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(block.content.buttonText || meta.label);

    const startEdit = () => {
        setDraft(block.content.buttonText || meta.label);
        setEditing(true);
    };
    const commitEdit = () => {
        const trimmed = draft.trim();
        if (trimmed && trimmed !== block.content.buttonText) {
            onContentChange({ buttonText: trimmed });
        }
        setEditing(false);
    };
    const cancelEdit = () => {
        setDraft(block.content.buttonText || meta.label);
        setEditing(false);
    };

    // Display label for the block row:
    //  - Info blocks always use the static meta.label ("Kalıp Bilgisi" / "Model Bilgisi")
    //  - Action blocks use the buttonText (editable) — merchant görür ki
    //    burada değiştirdiğinde widget'taki buton metni de aynı olur.
    const displayLabel = isAction ? (block.content.buttonText || meta.label) : meta.label;
    const description = isAction ? ACTION_DESC : (INFO_DESC[block.key] || "");

    return (
        <div
            className="grid items-center gap-2 px-5 py-3 hover:bg-muted/30 transition-colors"
            data-testid={`sp-block-${block.key}`}
        >
            <div className="grid items-center" style={{ gridTemplateColumns: "32px 12px 1fr auto" }}>
                <div className="h-8 w-8 shrink-0 rounded-md grid place-items-center bg-muted text-muted-foreground">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div aria-hidden="true" />
                <div className="min-w-0 pr-3">
                    {editing ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") commitEdit();
                                    if (e.key === "Escape") cancelEdit();
                                }}
                                autoFocus
                                className="h-8 text-sm"
                                placeholder={meta.label}
                                data-testid={`sp-block-inline-input-${block.key}`}
                            />
                            <button
                                type="button"
                                onClick={commitEdit}
                                className="shrink-0 h-8 w-8 grid place-items-center rounded-md text-foreground hover:bg-muted transition-colors"
                                aria-label="Kaydet"
                                data-testid={`sp-block-inline-save-${block.key}`}
                            >
                                <Check className="h-4 w-4" strokeWidth={2} />
                            </button>
                            <button
                                type="button"
                                onClick={cancelEdit}
                                className="shrink-0 h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:bg-muted transition-colors"
                                aria-label="İptal"
                                data-testid={`sp-block-inline-cancel-${block.key}`}
                            >
                                <X className="h-4 w-4" strokeWidth={2} />
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="text-sm font-medium flex items-center gap-1.5">
                                <span className="truncate" data-testid={`sp-block-label-${block.key}`}>
                                    {displayLabel}
                                </span>
                                {isAction && (
                                    <button
                                        type="button"
                                        onClick={startEdit}
                                        className="shrink-0 h-6 w-6 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                        aria-label="Buton metnini düzenle"
                                        data-testid={`sp-block-edit-${block.key}`}
                                    >
                                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </button>
                                )}
                            </div>
                            <div className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                                {description}
                            </div>
                        </>
                    )}
                </div>
                <Switch
                    checked={block.enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid={`sp-block-enable-${block.key}`}
                />
            </div>
        </div>
    );
};

/* ============================================================================
 * Template thumb (small typographic specimen for the picker)
 * ============================================================================ */
const TemplateThumb = ({ kind, active }) => {
    // Minimal typographic monogram — hiçbir şablon serif değildir, farklılık
    // sadece font-weight ve letter-spacing ile anlatılır.
    const SANS = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif';
    const mono = kind === "minimal"
        ? { text: "Aa", family: SANS, weight: 500, letter: "0", style: "normal" }   // Sade — regular medium
        : kind === "editorial"
            ? { text: "Aa", family: SANS, weight: 300, letter: "0.08em", style: "normal" }  // Editorial — light + wide
            : { text: "Aa", family: SANS, weight: 800, letter: "-0.04em", style: "normal" }; // Bold — heavy + tight
    return (
        <div className={`h-14 rounded-md grid place-items-center transition-colors ${active ? "bg-muted/60" : "bg-muted/30 group-hover:bg-muted/50"}`}>
            <span
                className={`text-2xl leading-none ${active ? "text-foreground" : "text-muted-foreground/70"}`}
                style={{ fontFamily: mono.family, fontWeight: mono.weight, letterSpacing: mono.letter, fontStyle: mono.style }}
            >
                {mono.text}
            </span>
        </div>
    );
};

/* ============================================================================
 * Panel Preview — 3 template DNAs with in-panel blocks
 * ============================================================================ */
const SANS = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",sans-serif';
const SERIF = 'Georgia,"Times New Roman",serif';
const SHADOW_CSS = { none: "none", soft: "0 1px 3px rgba(0,0,0,.07)", medium: "0 4px 12px rgba(0,0,0,.1)", dramatic: "0 10px 30px rgba(0,0,0,.16)" };

const PanelPreview = ({ template, theme, tokens, blocks, sample, groupOrder, fitLabels }) => {
    const dark = theme === "dark";
    const scale = tokens.fontScale || 1;
    const fs = (px) => `${Math.round(px * scale * 10) / 10}px`;
    const sp = tokens.spacing;
    const accA = adaptColor(tokens.accent, dark);
    const strong = dark ? "#F4F2EE" : "#1C1B19";
    const bg = dark ? "#1A1917" : "#FFFFFF";
    const border = dark ? "rgba(255,255,255,.14)" : "rgba(0,0,0,.1)";
    const hairline = dark ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.07)";
    const mutedColor = dark ? "#A6A29A" : "#6E6A63";
    const shadowCss = SHADOW_CSS[tokens.shadow];
    // Template DNA — hiçbir şablonda serif font kullanılmaz. Tüm template'ler
    // SANS; şablonlar sadece font-weight / letter-spacing / border-style gibi
    // CSS teknikleri ile farklılaşır.
    const family = FONT_STACK.sans;

    // ------ Template DNA flags ------
    const isEditorial = template === "editorial";          // Modern lüks sans, hairline, underline link, ikon yok (info rows)
    const isBold = template === "bold";                     // Field Notes ekolü — chunky weight, filled bg, 2px border
    const isSerifSade = template === "minimal";             // Sade — mevcut hali
    const editorialBg = dark ? "#1E1A15" : "#FAF7F2"; // warm ivory / warm charcoal
    const editorialSepia = dark ? "rgba(244,232,208,.16)" : "rgba(92,68,42,.14)";
    const editorialQuiet = dark ? "rgba(244,232,208,.08)" : "rgba(92,68,42,.09)";

    // ============================================================
    // UNIFIED HTML — Nihai widget DNA'sı. Tüm şablonlar (Sade, Zarif, Cesur)
    // aynı DOM yapısını render eder; sadece CSS theming ile görsel farklılaşırlar.
    // `data-theme={template}` root'a set edilir → gelecekte CSS ile
    // `[data-theme="zarif"] .sayz-...` gibi tema-spesifik override'lar yapılabilir.
    // Şu an tüm temalar Sade görselini paylaşır; Zarif/Cesur için theme CSS
    // eklendiğinde yalnızca CSS değişir, HTML dokunulmaz.
    // ============================================================
    const outerStyle = {
        fontFamily: family,
        color: strong,
        background: dark ? bg : "transparent",
        padding: 0,
    };

    const activeBlocks = blocks.filter((b) => {
        if (!b.enabled) return false;
        // Veri bazlı gizleme — ürüne tanımlı olmayan içerik için blok yüklenmez.
        //   fit_info   — ürüne 'Kalıp' atanmamışsa gizle (null = unset,
        //                merchant explicitly chose 'normal' → sample.fit var).
        //   model_info — ürüne model atanmamışsa gizle (ModelInfoBlock da
        //                içeride defansif null-check yapıyor; iki katman).
        //   size_table / find_my_size — beden tablosu yoksa VEYA pasifse
        //                hem CTA hem modal yüklenmez (LiveEmbed surface
        //                router'ı modali de erken null döner, bkz LiveEmbed.jsx).
        if (b.key === "fit_info" && !sample.fit) return false;
        if (b.key === "model_info" && !sample.model) return false;
        if ((b.key === "size_table" || b.key === "find_my_size") && !sample.table) return false;
        return true;
    });
    if (!activeBlocks.length) {
        return <div style={{ ...outerStyle, fontSize: fs(11), color: mutedColor, textAlign: "center" }}>Aktif blok yok.</div>;
    }

    const order = Array.isArray(groupOrder) && groupOrder.length === 2
        && groupOrder.includes("info") && groupOrder.includes("action")
        ? groupOrder : ["info", "action"];

    // Group blocks by semantic type — preserve intra-group order from blocks[]
    const infoBlocks = activeBlocks.filter((b) => BLOCK_META[b.key].group === "info");
    const actionBlocks = activeBlocks.filter((b) => BLOCK_META[b.key].group === "action");

    // ============================================================
    // UNIFIED SADE-DNA RENDERER — Nihai HTML iskeleti, tüm temalarda ortak.
    // Info group: 5-col aligned grid ([icon] [label] [:] [meta] [continuation]).
    // Action group: space-between bordered CTAs (secondary + primary).
    // `data-theme` attribute ile CSS-driven tema override edilebilir.
    // ============================================================
    const commonProps = { template, tokens, fs, sp, accA, strong, bg, border, hairline, mutedColor, dark };

        // Compute label/meta/continuation for each active info block.
        // Labels are single Turkish words ("Kalıp" / "Model") without the
        // suffix "Bilgisi" — cleaner alignment, no redundant word.
        const infoRows = infoBlocks.map((b) => {
            if (b.key === "fit_info") {
                const canon = FIT_CANONICAL[sample.fit.type] || "normal";
                const tenantLabels = (fitLabels && fitLabels[canon]) || {};
                const fitLabel = tenantLabels.label || FIT_DEFAULTS[canon].label;
                const advice = tenantLabels.advice || FIT_DEFAULTS[canon].advice;
                const showAdvice = b.content.showAdvice !== false && advice;
                return { key: "fit_info", icon: Shirt, label: "Kalıp", meta: fitLabel, cont: showAdvice ? advice : "" };
            }
            // model_info — merchant may not have assigned a model to this product.
            // In that case the backend sends `sample.model = null`; the block
            // simply skips rendering (no fake data). The row is filtered out
            // below so the surrounding grid doesn't allocate empty cells.
            const model = sample.model;
            if (!model) return null;
            const wornSize = model.wearsSize || "M";
            const showWorn = b.content.showWornSize !== false;
            const showMeas = b.content.showMeasurements !== false && (model.chest || model.waist || model.hip);
            // Meta: [wornSize] · [height cm] · [weight kg] — worn size başta,
            // sonra ölçüler `·` ile ayrılır. Örn: "M · 172 cm · 65 kg"
            const metaParts = [];
            if (showWorn) metaParts.push(wornSize);
            if (model.height) metaParts.push(`${model.height} cm`);
            if (model.weight) metaParts.push(`${model.weight} kg`);
            const hwStr = metaParts.join(" · ");
            // Cont: "Göğüs 92 · Bel 65 · Basen 85 cm" — her ölçünün etiketi var,
            // `·` ile ayrılıyor, sonda tek "cm" ile birim vurgulanır.
            let contStr = "";
            if (showMeas) {
                const measParts = [];
                if (model.chest) measParts.push(`Göğüs ${model.chest}`);
                if (model.waist) measParts.push(`Bel ${model.waist}`);
                if (model.hip) measParts.push(`Basen ${model.hip}`);
                if (measParts.length) contStr = measParts.join(" · ") + " cm";
            }
            return { key: "model_info", icon: UserRound, label: "Model", meta: hwStr, cont: contStr };
        });
        // Drop rows that opted out (model_info with no assigned model → null)
        const visibleInfoRows = infoRows.filter(Boolean);

        return (
            <div
                className="sayz-widget"
                data-theme={template}
                data-testid="sp-preview-block"
                style={outerStyle}
            >
                {/* Mobile responsive — ≤480px için layout düzeltmeleri.
                    Scoped: `.sayz-widget` sınıfı altında kalır, host sayfanın
                    diğer stillerine bulaşmaz. Shadow DOM içinde çalışsa da
                    light DOM admin preview'da da aynı davranır.

                    1) Info satırları (Kalıp / Model): mobilde "cont" (sağdaki
                       açıklama metni) kendi alt satırına düşer, tam genişlikte
                       sola yaslanır. Üstteki `[icon] [label] : [meta]` kısaldığı
                       için nefes alır.
                    2) Aksiyon CTA'ları: mobilde column'a döner, her CTA %100
                       genişlik. CTA içinde icon+text sola, ok sağa itilir
                       (text'e flex: 1 verilir — space-between ihtiyacı
                       olmadan tek değişiklikle).
                */}
                <style>{`
                    @media (max-width: 480px) {
                        .sayz-widget .sayz-info-group {
                            /* 4-kolon: icon, label, colon, meta — cont ayrı satıra */
                            grid-template-columns: auto auto auto 1fr !important;
                            row-gap: 6px !important;
                        }
                        .sayz-widget .sayz-info-group [data-slot="cont"] {
                            grid-column: 1 / -1 !important;
                            justify-self: start !important;
                            text-align: left !important;
                            margin-left: 0 !important;
                            margin-top: -2px;
                        }
                        .sayz-widget .sayz-info-group [data-slot="cont"]:empty {
                            display: none !important;
                        }
                        .sayz-widget .sayz-action-group {
                            flex-direction: column !important;
                            align-items: stretch !important;
                        }
                        .sayz-widget .sayz-action-group .sayz-action {
                            width: 100% !important;
                            justify-content: flex-start !important;
                            box-sizing: border-box;
                        }
                        /* Text'e flex: 1 verince kendisi büyür, ok doğal olarak
                           sağ uca itilir. İcon+text soldadır çünkü aynı sırada
                           kalıyorlar; sadece text genişleyen eleman. */
                        .sayz-widget .sayz-action-group .sayz-action [data-slot="action-text"] {
                            flex: 1 1 auto;
                            text-align: left;
                        }
                    }
                `}</style>
                {visibleInfoRows.length > 0 && (
                    <div
                        className="sayz-info-group"
                        data-testid="sp-preview-group-info"
                        style={{
                            display: "grid",
                            // 5 cols: [icon] [label] [:] [meta] [continuation]
                            // Her üç şablonda da ikon kolonu RENDER edilir.
                            // Stroke-width şablona göre uyarlanır (aşağıda).
                            gridTemplateColumns: "auto auto auto auto 1fr",
                            columnGap: 0,
                            rowGap: 8,
                            alignItems: "baseline",
                            fontSize: fs(WIDGET_TYPO.body),
                            lineHeight: 1.5,
                        }}
                    >
                        {visibleInfoRows.flatMap((r) => {
                            const IconComp = r.icon;
                            const cells = [];
                            // İkon TÜM şablonlarda render edilir, stroke-width şablona göre:
                            //   Sade (minimal) → 1.5 regular
                            //   Editorial      → 1 hairline (lüks)
                            //   Bold           → 1.75 chunky-lite (field notes nefes alan)
                            cells.push(
                                <span
                                    key={`${r.key}-icon`}
                                    className="sayz-icon"
                                    data-slot="icon"
                                    aria-hidden="true"
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifySelf: "start",
                                        marginRight: 6,
                                        transform: "translateY(2px)",
                                        color: accA,
                                    }}
                                >
                                    <IconComp
                                        size={fs(13)}
                                        strokeWidth={isBold ? 1.75 : isEditorial ? 1 : 1.5}
                                    />
                                </span>,
                            );
                            cells.push(
                                <span
                                    key={`${r.key}-label`}
                                    className="sayz-label"
                                    data-slot="label"
                                    data-testid={`sp-preview-info-${r.key}-label`}
                                    style={{
                                        color: accA,
                                        // Weight hiyerarşisi:
                                        //   Sade     → 400 regular
                                        //   Editorial→ 300 light + hafif tracking (lüks)
                                        //   Bold     → 650 heavy-lite (field notes nefes alan)
                                        fontWeight: isBold ? 650 : isEditorial ? 300 : 400,
                                        justifySelf: "start",
                                        letterSpacing: isEditorial ? "0.04em" : isBold ? "0.02em" : "normal",
                                    }}
                                >
                                    {r.label}
                                </span>,
                                <span
                                    key={`${r.key}-colon`}
                                    className="sayz-colon"
                                    data-slot="colon"
                                    aria-hidden="true"
                                    style={{
                                        color: accA,
                                        fontWeight: isBold ? 650 : isEditorial ? 300 : 400,
                                        marginLeft: 4,
                                    }}
                                >
                                    :
                                </span>,
                                <span
                                    key={`${r.key}-meta`}
                                    className="sayz-meta"
                                    data-slot="meta"
                                    data-testid={`sp-preview-info-${r.key}-meta`}
                                    style={{
                                        color: strong,
                                        // Meta değer vurgusu — Bold'da 650 heavy-lite, editorial 400 air, sade 500 medium
                                        fontWeight: isBold ? 650 : isEditorial ? 400 : 500,
                                        marginLeft: 8,
                                        letterSpacing: isEditorial ? "0.01em" : "normal",
                                    }}
                                >
                                    {r.meta || "—"}
                                </span>,
                                <span
                                    key={`${r.key}-cont`}
                                    className="sayz-cont"
                                    data-slot="cont"
                                    data-testid={`sp-preview-info-${r.key}-cont`}
                                    style={{
                                        color: mutedColor,
                                        fontWeight: isBold ? 500 : 300,
                                        marginLeft: 8,
                                        minWidth: 0,
                                        justifySelf: "end",
                                        textAlign: "right",
                                    }}
                                >
                                    {r.cont || ""}
                                </span>,
                            );
                            return cells;
                        })}
                    </div>
                )}
                {actionBlocks.length > 0 && (
                    <div
                        className="sayz-action-group"
                        data-testid="sp-preview-group-action"
                        style={{
                            marginTop: visibleInfoRows.length > 0 ? sp * 1.0 : 0,
                            display: "flex",
                            alignItems: "stretch",
                            justifyContent: "space-between",
                            gap: sp * 1.0,
                            width: "100%",
                            flexWrap: "wrap",
                        }}
                    >
                        <ActionRow2Col actionBlocks={actionBlocks} infoOffset={visibleInfoRows.length} single={actionBlocks.length === 1} {...commonProps} editorialSepia={editorialSepia} />
                    </div>
                )}
            </div>
        );
};

const EditorialDivider = ({ editorialSepia, editorialQuiet, mutedColor, sp, fs }) => (
    <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        marginTop: sp * 0.55,
        marginBottom: sp * 0.55,
    }}>
        <span style={{ flex: 1, height: 1, background: editorialQuiet }} />
        <span style={{ fontSize: fs(6.5), color: mutedColor, letterSpacing: ".2em", opacity: 0.65 }}>◆</span>
        <span style={{ flex: 1, height: 1, background: editorialQuiet }} />
    </div>
);

const editorialNumeral = (n) => {
    // "N°01" style
    return `N°${String(n).padStart(2, "0")}`;
};

/* ---------- Fit Info block preview ----------
 * Canonicalizes any fit alias to slim/normal/relaxed and reads tenant-customized
 * labels+advice from the sizing store (fitLabels). Defaults if store not hydrated.
 */
const FIT_CANONICAL = {
    slim: "slim", dar: "slim",
    normal: "normal",
    relaxed: "relaxed", loose: "relaxed", bol: "relaxed",
    genis: "relaxed", oversize: "relaxed",
};
const FIT_DEFAULTS = {
    slim:    { label: "Dar Kalıp",    advice: "Bir beden büyük almanızı öneririz." },
    normal:  { label: "Normal Kalıp", advice: "Kendi bedeninizi alabilirsiniz." },
    relaxed: { label: "Bol Kalıp",    advice: "Bir beden küçük almanızı öneririz." },
};
const FIT_POS = { slim: 12, dar: 12, normal: 50, relaxed: 82, loose: 82, bol: 82, genis: 82, oversize: 92 };

const FitInfoBlock = ({ template, tokens, c, sample, fs, sp, accA, strong, bg, border, hairline, mutedColor, dark, blockNumber, editorialSepia, editorialQuiet, fitLabels }) => {
    if (c.hideNormal && sample.fit.type === "normal") return null;
    const canon = FIT_CANONICAL[sample.fit.type] || "normal";
    const tenantLabels = (fitLabels && fitLabels[canon]) || {};
    const label = tenantLabels.label || FIT_DEFAULTS[canon].label;
    const advice = tenantLabels.advice || FIT_DEFAULTS[canon].advice;
    const pos = FIT_POS[sample.fit.type] ?? 50;

    if (template === "minimal") {
        // SADE — "Kültür Sayfası" DNA
        // Layout: küçük büyük harf label (KALIP) + serif değer (Normal Kalıp) + italik prose.
        // Dergi editorialı hissi — spec sheet değil, kısa curated bir yorum.
        const adviceInline = advice ? advice.replace(/[.!]+$/, "") + "." : "";
        return (
            <div style={{ fontFamily: SANS }}>
                <div style={{
                    fontSize: fs(9),
                    fontWeight: 500,
                    letterSpacing: ".22em",
                    color: mutedColor,
                    textTransform: "uppercase",
                    marginBottom: sp * 0.35,
                }}>
                    {c.title || "Kalıp"}
                </div>
                <div style={{
                    fontFamily: SERIF,
                    fontSize: fs(18),
                    fontWeight: 500,
                    lineHeight: 1.25,
                    color: strong,
                    letterSpacing: "-.005em",
                }}>
                    {label}
                </div>
                {c.showAdvice !== false && adviceInline && (
                    <div style={{
                        marginTop: sp * 0.3,
                        fontSize: fs(WIDGET_TYPO.body),
                        fontStyle: "italic",
                        color: mutedColor,
                        lineHeight: 1.55,
                        fontFamily: SANS,
                    }}>
                        {adviceInline}
                    </div>
                )}
            </div>
        );
    }

    // Fallback (Editorial / Bold henüz eklenmedi; şimdilik Sade DNA çıkar).
    return null;
};

/* Editorial fit gauge — 3-segment gauge that clearly shows the range.
 * Active segment is filled with accent; inactive segments are hairline-outlined.
 * Labels sit below, aligned center of each cell. Active label goes accent + non-italic.
 */
const EditorialFitGauge = ({ fitType, accA, editorialSepia, mutedColor, fs }) => {
    const cells = [
        { key: "dar", label: "Dar",    matches: ["slim", "dar"] },
        { key: "norm", label: "Normal", matches: ["normal"] },
        { key: "bol", label: "Bol",    matches: ["relaxed", "loose", "bol", "genis", "oversize"] },
    ];
    const activeIdx = cells.findIndex((c) => c.matches.includes(fitType));
    return (
        <div style={{ marginTop: 4 }}>
            <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                border: `1px solid ${editorialSepia}`,
            }}>
                {cells.map((c, i) => (
                    <div key={c.key} style={{
                        height: 8,
                        background: i === activeIdx ? accA : "transparent",
                        borderRight: i < cells.length - 1 ? `1px solid ${editorialSepia}` : "none",
                    }} />
                ))}
            </div>
            <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                marginTop: 6,
            }}>
                {cells.map((c, i) => {
                    const active = i === activeIdx;
                    return (
                        <div key={c.key} style={{
                            textAlign: "center",
                            fontFamily: SERIF,
                            fontStyle: active ? "normal" : "italic",
                            fontSize: fs(10.5),
                            color: active ? accA : mutedColor,
                            fontWeight: active ? 500 : 400,
                            letterSpacing: ".01em",
                        }}>
                            {c.label}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Indicator = ({ variant, pos, barGrad, markerColor, bg, mutedColor, fs, sp, editorialMode, editorialSepia }) => {
    if (editorialMode) {
        // Editorial: hairline scale with a serif triangle marker + italic axis labels
        return (
            <div>
                <div style={{ position: "relative", height: 1, background: editorialSepia }}>
                    {/* Tick marks — Dar, Normal, Bol positions */}
                    {[12, 50, 82].map((t) => (
                        <span key={t} style={{ position: "absolute", left: `${t}%`, top: -3, width: 1, height: 7, background: editorialSepia, transform: "translateX(-50%)" }} />
                    ))}
                    {/* Triangle marker */}
                    <span style={{
                        position: "absolute", left: `${pos}%`, top: -6,
                        width: 0, height: 0,
                        borderLeft: "5px solid transparent",
                        borderRight: "5px solid transparent",
                        borderTop: `6px solid ${markerColor}`,
                        transform: "translateX(-50%)",
                    }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontFamily: SERIF, fontStyle: "italic", fontSize: fs(9.5), color: mutedColor, letterSpacing: ".01em" }}>
                    <span>Dar</span><span>Normal</span><span>Bol</span>
                </div>
            </div>
        );
    }
    const h = variant === "bold" ? 6 : variant === "minimal" ? 3 : 2;
    const m = variant === "bold" ? 12 : variant === "minimal" ? 10 : 8;
    const bw = variant === "bold" ? 3 : 2;
    return (
        <div style={{ marginTop: sp * 0.7 }}>
            <div style={{ position: "relative", height: h, borderRadius: 999, background: barGrad }}>
                <div style={{ position: "absolute", top: "50%", left: `${pos}%`, width: m, height: m, borderRadius: "50%", background: bg, border: `${bw}px solid ${markerColor}`, transform: "translate(-50%,-50%)", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: fs(8), textTransform: "uppercase", letterSpacing: ".12em", color: mutedColor, fontWeight: 500 }}>
                <span>Dar</span><span>Normal</span><span>Bol</span>
            </div>
        </div>
    );
};

/* ---------- Model Info block preview ---------- */
const initials = (name) => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const ModelInfoBlock = ({ template, tokens, c, sample, fs, sp, accA, strong, border, mutedColor, dark, blockNumber, editorialSepia, editorialQuiet }) => {
    // Merchant may not have assigned a model to this product — in that case
    // the backend returns `sample.model = null`. We short-circuit instead of
    // rendering a block with "undefined cm" placeholders.
    if (!sample.model) return null;
    const model = sample.model;
    const wornSize = model.wearsSize || "M";
    const hwText = c.showHeightWeight !== false ? `${model.height} cm · ${model.weight} kg` : null;
    const measures = c.showMeasurements !== false ? [["Göğüs", model.chest], ["Bel", model.waist], ["Kalça", model.hip]] : [];
    const avatarKind = c.avatar || "monogram";
    const measureKind = c.measure || "underline";
    const avSize = template === "bold" ? 42 : template === "minimal" ? 34 : 22;
    const tint = pastelColor(tokens.accent, dark);

    const size = Math.round(avSize * (tokens.fontScale || 1));
    const ring = template !== "bold";
    const avBase = { width: size, height: size, borderRadius: "50%", border: ring ? `1px solid color-mix(in srgb, ${accA} 55%, transparent)` : "none", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
    const avatarEl = avatarKind === "monogram"
        ? <span style={{ ...avBase, background: `color-mix(in srgb, ${tint} 20%, transparent)`, color: accA, fontFamily: SANS, fontSize: size * 0.5, fontWeight: 800 }}>{wornSize}</span>
        : avatarKind === "initials"
            ? <span style={{ ...avBase, background: `color-mix(in srgb, ${tint} 22%, transparent)`, color: accA, fontFamily: SERIF, fontSize: size * 0.36, fontWeight: 500 }}>{initials(model.name)}</span>
            : <span style={{ ...avBase, color: accA }}><UserRound size={size * 0.55} strokeWidth={1.5} /></span>;

    const renderChip = (label, value) => {
        const baseStyle = { display: "inline-flex", alignItems: "baseline", gap: 5 };
        const lblSt = { fontSize: fs(measureKind === "boxed" ? 7.5 : 8.5), fontWeight: 600, textTransform: "uppercase", color: mutedColor, letterSpacing: measureKind === "pill" ? ".14em" : ".18em" };
        const valSt = { fontSize: fs(measureKind === "underline" ? 11 : 10.5), fontWeight: 700, color: strong, fontFeatureSettings: '"tnum" 1' };
        if (measureKind === "pill") return <span key={label} style={{ ...baseStyle, padding: "3px 9px", borderRadius: 999, border: `1px solid color-mix(in srgb, ${accA} 30%, transparent)`, background: dark ? "rgba(255,255,255,.04)" : `color-mix(in srgb, ${accA} 4%, transparent)`, alignItems: "center" }}><span style={lblSt}>{label}</span><span style={valSt}>{value}</span></span>;
        if (measureKind === "boxed") return <span key={label} style={{ display: "inline-flex", flexDirection: "column", padding: "3px 8px", borderRadius: 4, border: `1px solid ${border}`, lineHeight: 1.15 }}><span style={lblSt}>{label}</span><span style={valSt}>{value}</span></span>;
        return <span key={label} style={{ ...baseStyle, paddingBottom: 2, borderBottom: `1px solid color-mix(in srgb, ${accA} 45%, transparent)` }}><span style={lblSt}>{label}</span><span style={valSt}>{value}</span></span>;
    };

    if (template === "minimal") {
        // SADE — Single line. Height+weight (strong) + worn size and measurements
        // as muted continuation. No eyebrow, no icon, no model name. 13.5px base.
        const hwParts = [];
        if (model.height) hwParts.push(`${model.height} cm`);
        if (model.weight) hwParts.push(`${model.weight} kg`);
        const hwStr = hwParts.join(" · ");
        const continuation = [];
        if (c.showWornSize !== false) continuation.push(`${wornSize} beden giyiyor`);
        if (c.showMeasurements !== false && (model.chest || model.waist || model.hip)) {
            const meas = [model.chest, model.waist, model.hip].filter(Boolean).join("-");
            if (meas) continuation.push(`${meas} cm`);
        }
        const contStr = continuation.join(" · ");
        if (!hwStr && !contStr) return null;
        return (
            <div style={{
                display: "flex",
                alignItems: "baseline",
                gap: 5,
                flexWrap: "wrap",
                fontSize: fs(WIDGET_TYPO.body),
                lineHeight: 1.5,
                fontVariantNumeric: "tabular-nums",
            }}>
                {hwStr && <span style={{ fontWeight: 500, color: strong }}>{hwStr}</span>}
                {contStr && <span style={{ fontWeight: 400, color: mutedColor }}>{hwStr ? "· " : ""}{contStr}</span>}
            </div>
        );
    }
    if (template === "minimal") {
        // SADE — "Kültür Sayfası" DNA
        // Layout: MODEL label + serif "176 · 58 kg" değer + italik prose satırı.
        // Aynı Fit ile birebir simetri.
        const hwParts = [];
        if (model.height) hwParts.push(`${model.height} cm`);
        if (model.weight) hwParts.push(`${model.weight} kg`);
        const hwStr = hwParts.join(" · ");

        const hasWorn = c.showWornSize !== false && wornSize;
        const showHw = c.showHeightWeight !== false && hwStr;
        const showMeas = c.showMeasurements !== false && (model.chest || model.waist || model.hip);

        const measStr = showMeas
            ? [model.chest, model.waist, model.hip].filter(Boolean).join("-") + " cm"
            : "";

        // Prose: "Modelimiz M beden giyiyor (86-62-90 cm)."
        const proseParts = [];
        if (hasWorn) proseParts.push(`Modelimiz ${wornSize} beden giyiyor`);
        if (measStr && showMeas) proseParts.push(`(${measStr})`);
        const proseStr = proseParts.length ? proseParts.join(" ") + "." : "";

        if (!showHw && !proseStr) return null;

        return (
            <div style={{ fontFamily: SANS }}>
                <div style={{
                    fontSize: fs(9),
                    fontWeight: 500,
                    letterSpacing: ".22em",
                    color: mutedColor,
                    textTransform: "uppercase",
                    marginBottom: sp * 0.35,
                }}>
                    {c.title || "Model"}
                </div>
                {showHw && (
                    <div style={{
                        fontFamily: SERIF,
                        fontSize: fs(18),
                        fontWeight: 500,
                        lineHeight: 1.25,
                        color: strong,
                        letterSpacing: "-.005em",
                    }}>
                        {hwStr}
                    </div>
                )}
                {proseStr && (
                    <div style={{
                        marginTop: sp * 0.3,
                        fontSize: fs(WIDGET_TYPO.body),
                        fontStyle: "italic",
                        color: mutedColor,
                        lineHeight: 1.55,
                        fontFamily: SANS,
                    }}>
                        {proseStr}
                    </div>
                )}
            </div>
        );
    }
    return (
        <div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {avatarEl}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: fs(8.5), fontWeight: 700, letterSpacing: ".1em", color: accA }}>{c.title}</div>
                    {c.showName !== false && <div style={{ fontSize: fs(14), fontWeight: 800, marginTop: 3, letterSpacing: ".01em", lineHeight: 1.15, color: strong }}>{model.name}</div>}
                    <div style={{ fontSize: fs(11), color: mutedColor, marginTop: 3, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" }}>
                        {hwText && <span>{hwText}</span>}
                        {c.showWornSize !== false && <span>{hwText && <span style={{ opacity: 0.4, margin: "0 4px" }}>·</span>}<span style={{ color: accA, fontWeight: 700 }}>{wornSize}</span> beden</span>}
                    </div>
                </div>
            </div>
            {measures.length > 0 && <div style={{ marginTop: sp * 0.85, display: "flex", flexWrap: "wrap", gap: 8 }}>{measures.map(([l, v]) => renderChip(l, v))}</div>}
        </div>
    );
};

/* ============================================================================
 * ActionRow2Col — Sayz Widget Preview CTA row.
 *
 * SINGLE SOURCE OF TRUTH: `SayzCta` component. Bu row sadece `find_my_size` +
 * `size_table` bloklarını yerleştirir; ikon, padding, gap, radius, font-size,
 * arrow spec'i ne olursa olsun tümü `SayzCta` içinde tanımlıdır.
 *
 * Sayz Widget önizlemesi + `SizeChartModal` "Bedenimi Bul" cross-CTA + FMS
 * Wizard "Beden Tablosunu Gör" refusal CTA — HEPSİ aynı `SayzCta` çıktısını
 * render eder. Design spec değişikliği tek yerde yapılır: `SayzCta.jsx`.
 * ============================================================================ */
const ActionRow2Col = ({ actionBlocks, template, tokens }) => {
    return actionBlocks.map((b) => {
        const isPrimary = b.key === "find_my_size";
        const ActionIcon = isPrimary ? Sparkles : Table2;
        return (
            <SayzCta
                key={b.key}
                template={template}
                tokens={tokens}
                icon={ActionIcon}
                label={b.content.buttonText}
                variant={isPrimary ? "primary" : "secondary"}
                testId={`sp-preview-btn-${b.key}`}
            />
        );
    });
};

/* One column inside the 2-column action bar — ONLY the button, no eyebrow/title.
 * Button is self-describing (icon + text + arrow). Column alignment (left/right) drives
 * where the button rests inside its half of the row.
 */
const ActionCol = ({ c, kind, align, tokens, fs, sp, accA, strong, mutedColor }) => {
    const btnStyle = getEditorialButton(tokens.buttonStyle || "ghost", accA, fs, tokens.radius);
    const showIcon = (tokens.iconStyle || "outline") !== "none";
    const iconProps = tokens.iconStyle === "filled" ? { fill: "currentColor", strokeWidth: 1.5 } : { strokeWidth: 1.75 };
    const Icon = kind === "find_my_size" ? Sparkles : Ruler;
    return (
        <div style={{
            display: "flex",
            justifyContent: align === "right" ? "flex-end" : (align === "center" ? "center" : "flex-start"),
            alignItems: "center",
        }}>
            <button
                type="button"
                data-testid={`sp-preview-btn-${kind}`}
                style={{ ...btnStyle, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
            >
                {showIcon && <Icon size={Math.round(13 * (tokens.fontScale || 1))} {...iconProps} />}
                <span>{c.buttonText}</span>
                <span style={{ fontFamily: SERIF, fontSize: fs(15), lineHeight: 1, marginLeft: 3, opacity: 0.85 }}>→</span>
            </button>
        </div>
    );
};

/* Legacy row for minimal/bold templates — retained until Swiss/Field Notes DNAs land */
const ActionBlockLegacy = ({ c, kind, template, tokens, fs, sp, accA, strong, mutedColor }) => {
    const btnStyle = getButtonStyle(tokens.buttonStyle || "filled", accA, tokens.radius, fs, template);
    const Icon = kind === "find_my_size" ? Sparkles : Ruler;
    const showIcon = (tokens.iconStyle || "outline") !== "none";
    const iconProps = tokens.iconStyle === "filled" ? { fill: "currentColor", strokeWidth: 1.5 } : { strokeWidth: 2 };
    return (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: fs(8.5), letterSpacing: ".2em", color: template === "bold" ? accA : mutedColor, fontWeight: template === "bold" ? 700 : 500 }}>
                    {c.title}
                </div>
                <div style={{ fontSize: fs(WIDGET_TYPO.body), color: mutedColor, marginTop: 2, lineHeight: 1.4 }}>{c.description}</div>
            </div>
            <button type="button" style={{ ...btnStyle, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                {showIcon && <Icon size={13} {...iconProps} />}
                <span>{c.buttonText}</span>
            </button>
        </div>
    );
};

/* Heavier divider between Info group and Action group — editorial DNA */
const EditorialGroupDivider = ({ editorialSepia, mutedColor, sp, fs, accA }) => (
    <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 12, marginTop: sp * 1.1, marginBottom: sp * 1.0,
    }}>
        <span style={{ flex: 1, height: 1, background: editorialSepia }} />
        <span style={{ fontSize: fs(7.5), color: mutedColor, letterSpacing: ".24em", opacity: 0.75, fontFamily: SERIF, fontStyle: "italic" }}>◆</span>
        <span style={{ flex: 1, height: 1, background: editorialSepia }} />
    </div>
);

const getEditorialButton = (variant, accA, fs, radius) => {
    const base = {
        padding: "9px 14px",
        borderRadius: Math.min(radius, 4),
        fontFamily: SERIF,
        fontStyle: "italic",
        fontSize: fs(13),
        fontWeight: 400,
        letterSpacing: ".01em",
        transition: "all .18s ease",
    };
    if (variant === "outlined") {
        return { ...base, background: "transparent", color: accA, border: `1px solid color-mix(in srgb, ${accA} 60%, transparent)` };
    }
    if (variant === "ghost") {
        return { ...base, background: "transparent", color: accA, border: "none", padding: "6px 0", borderBottom: `1px solid color-mix(in srgb, ${accA} 55%, transparent)`, borderRadius: 0 };
    }
    // filled
    return { ...base, background: accA, color: "#FFFFFF", border: "none" };
};

const getButtonStyle = (variant, accA, radius, fs, template) => {
    const base = {
        padding: template === "bold" ? "9px 15px" : "8px 14px",
        borderRadius: Math.min(radius, 8),
        fontSize: fs(12),
        fontWeight: 600,
        transition: "all .18s ease",
    };
    if (variant === "outlined") {
        return { ...base, background: "transparent", color: accA, border: `1px solid color-mix(in srgb, ${accA} 55%, transparent)` };
    }
    if (variant === "ghost") {
        return { ...base, background: "transparent", color: accA, border: "none", padding: template === "bold" ? "9px 4px" : "8px 4px", textDecoration: "underline", textUnderlineOffset: 3 };
    }
    // filled (default)
    return { ...base, background: accA, color: "#FFFFFF", border: "none" };
};

// Named exports for live-embed widget (shared source of truth with panel preview)
export { PanelPreview, PreviewCanvas };
